import React, { useEffect, useState } from 'react';

// Hooks
import { useSelector } from 'react-redux';

// Actions
import { addFunds } from '~/redux/actions/stripe-actions.js';

// utils
import { MIN_FUNDING_LIMIT, ROLES } from '~/utils/constants.js';
import { MESSAGES } from '~/utils/message.js';
import { numberWithComma } from '~/utils/helperFunctions.js';

//MUI Component
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import {
  Box,
  Input,
  InputLabel,
  CircularProgress,
  InputAdornment,
} from '@mui/material';
// Icons
import PaymentMethod from '~/components/Account/Wallet/PaymentMethod';
import Stripe from '~/components//Stripe/Stripe';
import CloseIcon from '@mui/icons-material/Close';

// Styles
import './styles.scss';
import OlcButton from '~/components/General/OlcButton';
import OlcFormModal from '~/components/General/OlcFormModal';

const AddFunds = (props) => {
  const { handleClose, open, onSuccessCallback } = props;
  const user = useSelector((state) => state.userReducers?.user);
  const paymentMethods = useSelector((state) => state.stripeReducers?.methods) || [];
  const isUser = !(user?.role?.slug === ROLES['ORGANIZATION_ADMIN']);
  const [fund, setFund] = useState('');
  const [autoFunds, setAutoFunds] = useState(50);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [autoFundError, setAutoFundError] = useState('');
  const [respError, setRespError] = useState('');
  const [activeTab, setActiveTab] = useState(null);
  const [activatePaymentMethodModal, setActivatePaymentMethodModal] = useState(false);

  const validateFunding = (value, setErrorCallback) => {
    if (isNaN(value) || value < MIN_FUNDING_LIMIT) {
      setErrorCallback(MESSAGES.ACCOUNT.WALLET.STRIPE.ADD_FUNDING_VALIDATION);
      return false;
    } else {
      setErrorCallback('');
      return true;
    }
  };

  const handleAddFunding = async () => {
    const fundAmount = parseFloat(fund.toString().replaceAll(',', ''));
    const autoFundingAmount = parseFloat(autoFunds.toString().replaceAll(',', ''));
    const isFundValid = validateFunding(fundAmount, setError);
    const isAutoFundValid = validateFunding(autoFundingAmount, setAutoFundError);

    if (!isFundValid || !isAutoFundValid) return;
    setLoading((prev) => !prev);
    const data = await addFunds(fundAmount, autoFundingAmount);
    if (data.status === 200) {
      handleClose();
      onSuccessCallback && onSuccessCallback();
    } else {
      setRespError(data.data.message);
    }
    setLoading((prev) => !prev);
  };

  const handleChangeInput = (value, tab = false, isAutoFundAmount = false) => {
    if (!isUser && paymentMethods.length === 0) {
      return;
    }
    if (value.length > 8) return;
    if (typeof value == 'string') {
      let valueToAsign = value.replaceAll('$', '').replaceAll(',', '');
      if (valueToAsign !== '' && !/^-?\d*\.?\d{0,2}$/.test(valueToAsign)) return;
      valueToAsign = numberWithComma(valueToAsign);
      isAutoFundAmount ? setAutoFunds(valueToAsign) : setFund(valueToAsign);
    } else {
      value = numberWithComma(value);
      isAutoFundAmount ? setAutoFunds(value) : setFund(value);
    }
    if (error && value >= 50) {
      setError('');
    }
    if (autoFundError && value >= 50) {
      setAutoFundError('');
    }
    tab && setActiveTab(tab);
  };

  useEffect(() => {
    user?.organization?.minAutoFundingAmount > 50 &&
      setAutoFunds(user?.organization?.minAutoFundingAmount);
  }, [user]);

  const isAutoFundMinEmpty =
    autoFunds === null ||
    autoFunds === undefined ||
    autoFunds.toString().replaceAll('$', '').replaceAll(',', '').trim() === '';

  return (
    <>
      <OlcFormModal
        open={open}
        onClose={handleClose}
        title={MESSAGES.ACCOUNT.WALLET.STRIPE.ADD_FUNDS}
        className="walletAddFundsModal"
        footer={
          <>
            <OlcButton variant="secondary" onClick={handleClose}>
              {MESSAGES.ACCOUNT.WALLET.STRIPE.CANCEL}
            </OlcButton>
            <OlcButton
              variant="primary"
              disabled={loading || (!isUser && paymentMethods.length === 0) || isAutoFundMinEmpty}
              onClick={handleAddFunding}
            >
              {loading ? (
                <CircularProgress
                  sx={{
                    color: 'white',
                    width: '25px !important',
                    height: '25px !important',
                  }}
                />
              ) : (
                MESSAGES.ACCOUNT.WALLET.STRIPE.CONFIRM
              )}
            </OlcButton>
          </>
        }
      >
        {!isUser && paymentMethods.length === 0 && (
          <Box className="noPaymentMethodText">
            <Typography variant="body2" color="error" fontWeight={600}>
              You have no payment method{' '}
              <span
                className="addPaymentMethod"
                onClick={() => setActivatePaymentMethodModal(true)}
              >
                Add Payment Method
              </span>
            </Typography>
          </Box>
        )}
        <Box className="amountInputWrapper">
          <InputLabel
            className={
              !isUser && paymentMethods.length === 0
                ? 'disabledFundFieldLabel'
                : 'fundsLabelWallet'
            }
          >
            {MESSAGES.ACCOUNT.WALLET.STRIPE.ENTER_AMOUNT}
            <span className="olcRequired">*</span>
          </InputLabel>
          <InputLabel
            className={
              !isUser && paymentMethods.length === 0
                ? 'disabledFundFieldSubLabel'
                : 'fundsLabelWalletSubText'
            }
          >
            {MESSAGES.ACCOUNT.WALLET.STRIPE.ENTER_AMOUNT_SUB_TEXT}
          </InputLabel>
          <Input
            className="fundsInputFieldWallet"
            type="text"
            placeholder="$10,000.00"
            value={fund ? `$${fund}` : fund}
            onChange={(e) => handleChangeInput(e.target.value)}
            disabled={!isUser && paymentMethods.length === 0}
            endAdornment={
              <InputAdornment position="end" sx={{ width: '16px', marginRight: '8px' }}>
                {fund && (
                  <IconButton
                    className="cpClearInputBtn"
                    size="small"
                    onClick={() => {
                      setFund('');
                    }}
                    edge="end"
                    sx={{ padding: '4px' }}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                )}
              </InputAdornment>
            }
          />
          {error && <Typography className="errorMessage">{error}</Typography>}
          <Box className="amountsWrapper">
            <Typography
              onClick={() => handleChangeInput(500, 1)}
              className={`amountBtnWallet ${!isUser && paymentMethods.length === 0 && 'disabled'} ${activeTab === 1 ? 'activeAmount' : ''}`}
            >
              $500
            </Typography>
            <Typography
              onClick={() => handleChangeInput(1000, 2)}
              className={`amountBtnWallet ${!isUser && paymentMethods.length === 0 && 'disabled'} ${activeTab === 2 ? 'activeAmount' : ''}`}
            >
              $1,000
            </Typography>
            <Typography
              onClick={() => handleChangeInput(2500, 3)}
              className={`amountBtnWallet ${!isUser && paymentMethods.length === 0 && 'disabled'} ${activeTab === 3 ? 'activeAmount' : ''}`}
            >
              $2,500
            </Typography>
            <Typography
              onClick={() => handleChangeInput(5000, 4)}
              className={`amountBtnWallet ${!isUser && paymentMethods.length === 0 && 'disabled'} ${activeTab === 4 ? 'activeAmount' : ''}`}
            >
              $5,000
            </Typography>
            <Typography
              onClick={() => handleChangeInput(10000, 5)}
              className={`amountBtnWallet ${!isUser && paymentMethods.length === 0 && 'disabled'} ${activeTab === 5 ? 'activeAmount' : ''}`}
            >
              $10,000
            </Typography>
          </Box>

          <Box
            className={`walletCheckBoxAutoFundingWrapper ${!isUser && paymentMethods.length === 0 && 'disabled'}`}
            style={{ marginTop: '12px' }}
          >
            <InputLabel className="fundsLabelWallet">
              {MESSAGES.ACCOUNT.WALLET.STRIPE.ENTER_MIN_AUTO_AMOUNT}
            </InputLabel>
            <Input
              className="fundsInputFieldWallet"
              sx={{ mt: '8px' }}
              type="text"
              placeholder="$50"
              disabled={!isUser && paymentMethods.length === 0}
              value={autoFunds ? `$${autoFunds}` : autoFunds}
              onChange={(e) => handleChangeInput(e.target.value, false, true)}
              endAdornment={
                <InputAdornment position="end" sx={{ width: '16px', marginRight: '8px' }}>
                  {autoFunds && (
                    <IconButton
                      className="cpClearInputBtn"
                      size="small"
                      onClick={() => setAutoFunds('')}
                      edge="end"
                      sx={{ padding: '4px' }}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  )}
                </InputAdornment>
              }
            />
            {autoFundError && (
              <Typography className="errorMessage">{autoFundError}</Typography>
            )}
          </Box>
        </Box>
        {respError && <Typography className="fundsModalContent__error">{respError} </Typography>}
      </OlcFormModal>
      <div style={{ position: 'absolute' }}>
        <Stripe>
          <PaymentMethod
            open={activatePaymentMethodModal}
            handleOpen={() => {
              setActivatePaymentMethodModal(true);
            }}
            handleClose={() => {
              setActivatePaymentMethodModal(false);
            }}
          />
        </Stripe>
      </div>
    </>
  );
};

export default AddFunds;
